import {
  BookOpen,
  GraduationCap,
  Users,
  VideoIcon,
  FileText,
  User,
  UserStar,
  Loader2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { dashboardNavTileData } from "../constant/data";
import { getDashboardStatsAPI } from "../services/apicollection";

function NavTile({ icon, title, href, desc }) {
  return (
    <Link
      to={href}
      className="group flex items-center gap-4 rounded-xl border border-neutral-200 bg-[#0f1c2e] p-4 transition-all hover:border-neutral-300 hover:bg-neutral-50"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700 transition group-hover:bg-neutral-200">
        {icon}
      </div>
      <div className="text-sm font-medium text-neutral-800">
        <span>{title}</span>
        <p className="text-xs text-blue-600/50">{desc}</p>
      </div>
    </Link>
  );
}

const STAT_TILES = [
  { key: "courses", label: "Courses", icon: GraduationCap, color: "bg-blue-500" },
  { key: "batches", label: "Batches", icon: BookOpen, color: "bg-emerald-500" },
  { key: "students", label: "Students", icon: Users, color: "bg-teal-500" },
  { key: "webinars", label: "Webinars", icon: VideoIcon, color: "bg-violet-500" },
  { key: "blogs", label: "Blogs", icon: FileText, color: "bg-amber-500" },
  { key: "courseContents", label: "Course Content", icon: BookOpen, color: "bg-cyan-500" },
  { key: "instructors", label: "Faculty", icon: User, color: "bg-rose-500" },
  { key: "reviews", label: "Reviews", icon: UserStar, color: "bg-indigo-500" },
];

function StatTile({ label, value, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-[#0f1c2e] p-4 shadow-sm">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${color} text-white`}
      >
        <Icon size={24} />
      </div>
      <div>
        <p className="text-2xl font-bold text-neutral-800">{value}</p>
        <p className="text-xs font-medium text-neutral-500">{label}</p>
      </div>
    </div>
  );
}

const MainDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchStats = async () => {
      try {
        const data = await getDashboardStatsAPI();
        if (mounted) setStats(data);
      } catch {
        if (mounted) setStats({});
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchStats();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="space-y-8">
      {/* Stats section */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-slate-200">
          Dashboard Overview
        </h2>
        {loading ? (
          <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-[#0f1c2e] p-6">
            <Loader2 size={20} className="animate-spin text-blue-600" />
            <span className="text-sm text-neutral-500">Loading stats...</span>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {STAT_TILES.map((tile) => (
              <StatTile
                key={tile.key}
                label={tile.label}
                value={stats?.[tile.key] ?? "—"}
                icon={tile.icon}
                color={tile.color}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quick navigation */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-slate-200">
          Quick Navigation
        </h2>
        <p className="mb-4 text-sm text-neutral-500">
          Navigate to any feature to manage your services
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {dashboardNavTileData.map((item) => (
            <NavTile
              key={item.id}
              icon={item.icon}
              title={item.title}
              href={item.href}
              desc={item.desc}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default MainDashboardPage;
